// 보전 (Maintenance) — 명세서 FR4: 예방·예지 보전 + MTBF/MTTR
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { Counter } from '../components/Counter'
import { Skeleton, SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useAuth, hasPerm } from '../lib/auth'
import { useT } from '../lib/i18n'

interface Plan {
  plan_id: string
  equipment_id: string
  equipment_name: string
  name: string
  type: 'preventive' | 'predictive' | 'calibration' | 'overhaul'
  trigger_kind: 'interval_days' | 'runtime_hours' | 'cycle_count'
  trigger_value: number
  estimated_minutes: number
  last_done_at: string | null
  next_due_at: string | null
  active: number
}

interface Order {
  mo_id: string
  equipment_id: string
  equipment_name: string
  plan_id: string | null
  kind: 'preventive' | 'corrective' | 'emergency'
  status: 'open' | 'in_progress' | 'done' | 'cancelled'
  scheduled_at: string | null
  started_at: string | null
  completed_at: string | null
  assigned_to: string | null
  assignee_name: string | null
  problem: string | null
  resolution: string | null
  downtime_minutes: number | null
}

interface Reliability {
  equipment_id: string
  equipment_name: string
  window_days: number
  failures: number
  observationHours: number
  totalDowntimeMinutes: number
  mtbfHours: number | null
  mttrMinutes: number | null
  availabilityPercent: number | null
}

const KIND_BADGE: Record<Order['kind'], string> = {
  preventive: 'info', corrective: 'warn', emergency: 'danger',
}
const STATUS_BADGE: Record<Order['status'], string> = {
  open: '', in_progress: 'info', done: 'success', cancelled: '',
}

function dueIn(iso: string | null): { text: string; class: string } {
  if (!iso) return { text: '—', class: '' }
  const ms = new Date(iso).getTime() - Date.now()
  const d = Math.round(ms / (1000 * 60 * 60 * 24))
  if (d < 0) return { text: `${Math.abs(d)}일 경과`, class: 'danger' }
  if (d === 0) return { text: '오늘', class: 'warn' }
  if (d <= 7) return { text: `${d}일 후`, class: 'warn' }
  return { text: `${d}일 후`, class: '' }
}

function fmtMin(mins: number | null): string {
  if (mins === null || mins === undefined) return '—'
  if (mins < 60) return `${mins.toFixed(0)}분`
  const h = Math.floor(mins / 60)
  const m = Math.round(mins % 60)
  return `${h}시간 ${m}분`
}

export function MaintenancePage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()
  const canWrite = hasPerm(user, 'maintenance', 'write')

  const [plans, setPlans] = useState<Plan[] | null>(null)
  const [orders, setOrders] = useState<Order[] | null>(null)
  const [rel, setRel] = useState<Reliability[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [completeFor, setCompleteFor] = useState<{ id: string; resolution: string; downtime: string } | null>(null)

  const load = async () => {
    try {
      const [p, o, r] = await Promise.all([
        api.get<Plan[]>('/maintenance/plans'),
        api.get<Order[]>('/maintenance/orders'),
        api.get<Reliability[]>('/maintenance/reliability?days=30'),
      ])
      setPlans(p); setOrders(o); setRel(r)
    } catch (e: any) { toast.push('error', e?.message ?? '조회 실패') }
  }
  useEffect(() => { load() }, [])

  const start = async (id: string) => {
    setBusy(id)
    try { await api.post(`/maintenance/orders/${id}/start`); toast.push('success', '작업 시작'); load() }
    catch (e: any) { toast.push('error', e?.message ?? '실패') }
    finally { setBusy(null) }
  }

  const complete = async () => {
    if (!completeFor) return
    setBusy(completeFor.id)
    try {
      await api.post(`/maintenance/orders/${completeFor.id}/complete`, {
        resolution: completeFor.resolution,
        downtime_minutes: completeFor.downtime ? Number(completeFor.downtime) : undefined,
      })
      toast.push('success', '작업 완료')
      setCompleteFor(null)
      load()
    } catch (e: any) { toast.push('error', e?.message ?? '실패') }
    finally { setBusy(null) }
  }

  const orderRowsOpen = orders?.filter((o) => o.status === 'open' || o.status === 'in_progress') ?? []
  const orderRowsDone = orders?.filter((o) => o.status === 'done').slice(0, 20) ?? []

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="wrench" /> {t('maint.title')}
      </h1>
      <p className="muted reveal">{t('maint.intro')}</p>

      {/* Reliability KPI */}
      <section
        aria-labelledby="rel-h"
        className="reveal"
        style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))' }}
      >
        <h2 id="rel-h" className="sr-only">신뢰성 KPI</h2>
        {!rel
          ? Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} glass tilt={false} spotlight={false}><Skeleton h={120} /></Card>
            ))
          : rel.map((r) => {
              const grade = r.availabilityPercent ?? 0
              const color = grade >= 99 ? 'success' : grade >= 95 ? 'info' : grade >= 90 ? 'warn' : 'danger'
              return (
                <Card key={r.equipment_id} glass tilt spotlight>
                  <div className="row space-between">
                    <span className="badge info">{r.equipment_id}</span>
                    <span className={`badge ${color}`}>{r.failures === 0 ? '무결' : `${r.failures}회 고장`}</span>
                  </div>
                  <h3 style={{ marginBlock: '0.3rem 0.4rem', fontSize: 'var(--fs-lg)' }}>{r.equipment_name}</h3>
                  <p className="kpi-num" style={{ margin: 0 }}>
                    <Counter value={r.availabilityPercent ?? 100} decimals={2} suffix="%" />
                  </p>
                  <p className="muted" style={{ margin: '0.2rem 0 0.8rem', fontSize: 'var(--fs-sm)' }}>가용률 (30일)</p>
                  <div className="row" style={{ fontSize: 'var(--fs-sm)', gap: '0.8rem' }}>
                    <span><span className="muted">MTBF</span> <span className="mono">{r.mtbfHours === null ? '—' : `${r.mtbfHours.toFixed(0)}h`}</span></span>
                    <span><span className="muted">MTTR</span> <span className="mono">{fmtMin(r.mttrMinutes)}</span></span>
                  </div>
                </Card>
              )
            })}
      </section>

      {/* PM 계획 */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="plans-h">
        <h2 id="plans-h" style={{ fontSize: 'var(--fs-lg)' }}>예방 보전 계획</h2>
        {!plans ? (
          <SkeletonRows rows={4} />
        ) : plans.length === 0 ? (
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="wrench" className="icon-lg" />
            <p className="muted" style={{ margin: 0 }}>활성 보전 계획이 없습니다.</p>
          </div>
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '880px' } as React.CSSProperties}>
            <table className="table" aria-label="보전 계획">
              <thead>
                <tr>
                  <th>설비</th><th>계획명</th><th>유형</th><th>주기</th><th>예상</th><th>최근 수행</th><th>만기</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => {
                  const d = dueIn(p.next_due_at)
                  return (
                    <tr key={p.plan_id}>
                      <td className="mono">{p.equipment_id}</td>
                      <td>{p.name}</td>
                      <td><span className="badge">{p.type}</span></td>
                      <td className="mono">{p.trigger_value} {p.trigger_kind === 'interval_days' ? '일' : p.trigger_kind === 'runtime_hours' ? '시간' : '회'}</td>
                      <td className="mono">{p.estimated_minutes}분</td>
                      <td>{p.last_done_at ? new Date(p.last_done_at).toLocaleDateString('ko-KR') : '—'}</td>
                      <td><span className={`badge ${d.class}`}>{d.text}</span></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 진행/예정 작업 */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="open-h">
        <h2 id="open-h" style={{ fontSize: 'var(--fs-lg)' }}>진행/예정 작업 ({orderRowsOpen.length})</h2>
        {!orders ? (
          <SkeletonRows rows={3} />
        ) : orderRowsOpen.length === 0 ? (
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="clipboard-check" className="icon-lg" style={{ color: 'var(--color-success)' }} />
            <p className="muted" style={{ margin: 0 }}>처리 대기 중인 작업이 없습니다.</p>
          </div>
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '900px' } as React.CSSProperties}>
            <table className="table" aria-label="진행 중 보전 작업">
              <thead>
                <tr><th>작업</th><th>설비</th><th>유형</th><th>상태</th><th>담당</th><th>증상/예정</th><th></th></tr>
              </thead>
              <tbody>
                {orderRowsOpen.map((o) => (
                  <tr key={o.mo_id}>
                    <td className="mono">{o.mo_id}</td>
                    <td>{o.equipment_id}</td>
                    <td><span className={`badge ${KIND_BADGE[o.kind]}`}>{o.kind}</span></td>
                    <td><span className={`badge ${STATUS_BADGE[o.status]}`}>{o.status}</span></td>
                    <td>{o.assignee_name ?? o.assigned_to ?? '—'}</td>
                    <td style={{ maxWidth: 280 }}>
                      {o.problem ?? (o.scheduled_at ? new Date(o.scheduled_at).toLocaleString('ko-KR') : '—')}
                    </td>
                    <td>
                      <div className="row" style={{ gap: '0.4rem' }}>
                        {o.status === 'open' && (
                          <Button variant="ghost" magnetic={false} disabled={!canWrite || busy === o.mo_id}
                            onClick={() => start(o.mo_id)} aria-label={`${o.mo_id} 시작`}>
                            <FontAwesomeIcon icon={busy === o.mo_id ? 'circle-notch' : 'play'} spin={busy === o.mo_id} />
                            <span>시작</span>
                          </Button>
                        )}
                        <Button variant="accent" magnetic={false} disabled={!canWrite}
                          onClick={() => setCompleteFor({ id: o.mo_id, resolution: '', downtime: o.kind === 'preventive' ? '0' : '' })}
                          aria-label={`${o.mo_id} 완료`}>
                          <FontAwesomeIcon icon="check" />
                          <span>완료</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 최근 완료 */}
      {orderRowsDone.length > 0 && (
        <Card glass tilt={false} spotlight={false} className="reveal" as="section">
          <h2 style={{ fontSize: 'var(--fs-lg)' }}>최근 완료 ({orderRowsDone.length})</h2>
          <div className="table-wrap" style={{ '--table-min-w': '820px' } as React.CSSProperties}>
            <table className="table" aria-label="최근 완료 보전 작업">
              <thead>
                <tr><th>작업</th><th>설비</th><th>유형</th><th>완료</th><th>다운타임</th><th>조치</th></tr>
              </thead>
              <tbody>
                {orderRowsDone.map((o) => (
                  <tr key={o.mo_id}>
                    <td className="mono">{o.mo_id}</td>
                    <td>{o.equipment_id}</td>
                    <td><span className={`badge ${KIND_BADGE[o.kind]}`}>{o.kind}</span></td>
                    <td>{o.completed_at ? new Date(o.completed_at).toLocaleString('ko-KR') : '—'}</td>
                    <td className="mono">{o.downtime_minutes !== null ? `${o.downtime_minutes}분` : '—'}</td>
                    <td style={{ maxWidth: 320 }}>{o.resolution ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* 완료 모달 */}
      {completeFor && (
        <div role="dialog" aria-modal="true" aria-label="작업 완료"
          onClick={(e) => { if (e.target === e.currentTarget) setCompleteFor(null) }}
          style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'grid', placeItems: 'center', padding: '1rem' }}
        >
          <Card glass tilt={false} spotlight={false} style={{ width: 'min(520px, 100%)' }}>
            <h2 style={{ fontSize: 'var(--fs-lg)' }}>작업 완료 — {completeFor.id}</h2>
            <div className="field">
              <label htmlFor="reso">조치 내용 (필수)</label>
              <textarea id="reso" rows={3} placeholder="예: 베어링 교체 + 윤활유 보충"
                value={completeFor.resolution}
                onChange={(e) => setCompleteFor({ ...completeFor, resolution: e.target.value })}
                style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-line-strong)', background: 'var(--color-surface)' }} />
            </div>
            <div className="field">
              <label htmlFor="dt">가동중단 시간(분) — 예방보전이면 0</label>
              <input id="dt" type="number" min="0" placeholder="예: 75"
                value={completeFor.downtime}
                onChange={(e) => setCompleteFor({ ...completeFor, downtime: e.target.value })} />
            </div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Button variant="ghost" magnetic={false} onClick={() => setCompleteFor(null)}>취소</Button>
              <Button variant="accent" magnetic={false}
                disabled={!completeFor.resolution.trim() || busy === completeFor.id}
                onClick={complete}>
                <FontAwesomeIcon icon={busy === completeFor.id ? 'circle-notch' : 'check'} spin={busy === completeFor.id} />
                <span>완료 처리</span>
              </Button>
            </div>
          </Card>
        </div>
      )}
    </main>
  )
}
